<script setup lang="ts">
import type { IInsuranceProduct } from '~/components/entity/insurance-product/types'
import { Badge } from '~/components/ui/badge'

const KGS_CURRENCY_ID = 1

interface Props {
  item: IInsuranceProduct
}

const props = defineProps<Props>()
const { t } = useI18n()

const forWhomList = computed(() => {
  const forWhom = props.item.for_whom
  if (!forWhom) return []
  return Object.keys(forWhom).filter((key) => forWhom[key])
})

const formatRange = (
  from: number | null,
  to: number | null,
  rangeKey: string,
  formatValue: (value: number) => string
): string | null => {
  if (from === null && to === null) return null
  if (from !== null && to !== null && from !== to) {
    return t(rangeKey, { from: formatValue(from), to: formatValue(to) })
  }
  return formatValue((from ?? to) as number)
}

// The caller always passes a real number, so the format never returns null.
const formatAmount = (value: number) => useCurrencyFormat(value, KGS_CURRENCY_ID) ?? ''

const premium = computed(() =>
  formatRange(
    props.item.premium_from,
    props.item.premium_to,
    'entity.insurance_product.item.amount_range',
    formatAmount
  )
)
const coverage = computed(() =>
  formatRange(
    props.item.coverage_min,
    props.item.coverage_max,
    'entity.insurance_product.item.amount_range',
    formatAmount
  )
)
const duration = computed(() =>
  formatRange(
    props.item.duration_min,
    props.item.duration_max,
    'entity.insurance_product.item.duration_range',
    (value) => t('entity.insurance_product.item.duration_months', { count: value })
  )
)
const insuredAge = computed(() =>
  formatRange(
    props.item.insured_age_min,
    props.item.insured_age_max,
    'entity.insurance_product.item.age_range',
    String
  )
)
</script>

<template>
  <article class="Card">
    <div class="space-y-3">
      <Heading tag="h3" class="hit-title h6">{{ props.item.title }}</Heading>

      <div
        v-if="props.item.description"
        v-hydrated-html="props.item.description"
        class="text-sm text-gray-600"
      />

      <div class="grid grid-cols-2 gap-x-4 gap-y-3 md:grid-cols-3">
        <div v-if="premium">
          <div class="SecondaryTextHeadings">
            {{ $t('entity.insurance_product.item.premium_label') }}
          </div>
          <div class="font-semibold">{{ premium }}</div>
        </div>
        <div v-if="coverage">
          <div class="SecondaryTextHeadings">
            {{ $t('entity.insurance_product.item.coverage_label') }}
          </div>
          <div class="font-semibold">{{ coverage }}</div>
        </div>
        <div v-if="duration">
          <div class="SecondaryTextHeadings">
            {{ $t('entity.insurance_product.item.duration_label') }}
          </div>
          <div class="font-semibold">{{ duration }}</div>
        </div>
        <div v-if="insuredAge">
          <div class="SecondaryTextHeadings">
            {{ $t('entity.insurance_product.item.insured_age_label') }}
          </div>
          <div class="font-semibold">{{ insuredAge }}</div>
        </div>
      </div>
    </div>
    <footer
      v-if="props.item.insurance_type || forWhomList.length"
      class="mt-4 flex flex-wrap gap-2"
    >
      <Badge v-if="props.item.insurance_type" variant="gray" class="text-xs" rounded="full">
        {{ props.item.insurance_type.title }}
      </Badge>
      <Badge v-for="whom in forWhomList" :key="whom" variant="gray" class="text-xs" rounded="full">
        {{ $t(`widget.insurances.list_meili_search.for_whom_options.${whom}`) }}
      </Badge>
    </footer>
  </article>
</template>
